import React from 'react';

import { Box, Stack } from '@mui/material';

import type { IncidentMaxCellTMonitorProps } from './types';
import { useMaxCellTMonitor } from './hooks/useMaxCellTMonitor';
import { useConnectionStatus } from './hooks/useConnectionStatus';
import { useSelectedUnitNames } from './hooks/useSelectedUnitNames';
import IncidentDataGrid from './components/DataGrid/IncidentDataGrid';
import { useIncidentColumns } from './hooks/useIncidentColumns';
import IncidentAlerts from './components/Alerts/IncidentAlerts';
import { SPACING } from './styles';
import { ChartModalProvider } from './hooks/useChartModalState';
import ChartModal from './components/Chart/ChartModal';
import LoadingState from './components/LoadingState/LoadingState';
import ErrorState from './components/ErrorState/ErrorState';
import UnitFilterDisplay from './components/UnitFilterDisplay/UnitFilterDisplay';
import HeaderSection from './components/HeaderSection/HeaderSection';
import HelpButton from './components/HelpButton/HelpButton';

const COMPONENT_NAME = 'IncidentMaxCellTMonitor';

const IncidentMaxCellTMonitor: React.FC<IncidentMaxCellTMonitorProps> = (
  props,
) => {
  const { selectedUnits, siteMeta, timeMode } = props;

  const { incidents, loading, error, lastDataUpdate } =
    useMaxCellTMonitor(props);

  const connectionStatus = useConnectionStatus();
  const selectedUnitNames = useSelectedUnitNames(selectedUnits, siteMeta);
  const columns = useIncidentColumns(timeMode, siteMeta);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <ErrorState message={typeof error === 'string' ? error : undefined} />
    );
  }
  return (
    <ChartModalProvider>
      <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
        <HelpButton />

        <UnitFilterDisplay unitNames={selectedUnitNames} />

        <HeaderSection
          connectionStatus={connectionStatus}
          lastDataUpdate={lastDataUpdate}
          timeMode={timeMode}
          siteMeta={siteMeta}
        />

        <Stack spacing={SPACING.MEDIUM}>
          <IncidentAlerts incidents={incidents} />

          {incidents.length > 0 && (
            <IncidentDataGrid incidents={incidents} columns={columns} />
          )}
        </Stack>
      </Box>

      <ChartModal />
    </ChartModalProvider>
  );
};

IncidentMaxCellTMonitor.displayName = COMPONENT_NAME;

export default IncidentMaxCellTMonitor;
